/**
 * AuthShellLayout — chrome PERSISTANT partagé par les pages d'authentification.
 *
 * À ne pas confondre avec AuthLayout : celui-ci est un layout React Router
 * (rendu via <Outlet />), monté UNE FOIS pour toute la zone auth. Il porte :
 *   - Le fond crème champagne-50
 *   - Le BubblesCanvas (animation de bulles en arrière-plan)
 *   - Le header (wordmark Crayéa)
 *   - Le footer (réassurance conformité)
 *
 * Pourquoi ce découpage : le BubblesCanvas garde son état interne (positions,
 * vitesses, phases des bulles) tant qu'il n'est pas démonté. En l'isolant
 * dans une route parent, la navigation /login ↔ /signup ↔ /reset-password
 * ↔ /magic-link ne démonte que les pages enfants — le canvas survit, les
 * bulles ne se réinitialisent plus à chaque changement de page.
 *
 * Le contenu propre à chaque page (titre, carte du formulaire, footer
 * spécifique) reste géré par AuthLayout, qui est désormais une simple
 * "card" centrée — sans chrome.
 */

import { Skeleton } from "@champ-erp/ui";
import { Suspense, useEffect } from "react";
import { Outlet } from "react-router-dom";

import { BubblesCanvas } from "../components/BubblesCanvas.js";
import { hubOriginForCurrentHost } from "../lib/tenant-host.js";

/**
 * Si on est sur un sous-domaine tenant (`<slug>.crayea.fr` /
 * `<slug>.localhost`), retourne l'URL absolue du hub correspondant ;
 * sinon `null`. Un sous-domaine tenant ne doit jamais servir de pages
 * d'authentification (cf docs/multitenancy.md § Principes : seul le hub
 * authentifie). Si l'utilisateur atterrit ici par erreur (lien partagé,
 * bookmark, redirect 401 d'une session expirée), on le renvoie vers le
 * hub en préservant son chemin et ses query params.
 */
function detectHubBaseUrl(): string | null {
  if (typeof window === "undefined") return null;
  return hubOriginForCurrentHost(window.location);
}

/**
 * Pathnames sous AuthShellLayout qui DOIVENT pouvoir s'exécuter sur un
 * sous-domaine tenant. Tous les autres (login, signup, reset-password,
 * magic-link, invitation, aucun-domaine, choisir-domaine) ne sont
 * légitimes que sur le hub.
 *
 * - `/auth/handoff` : consume du token SSO arrive nécessairement sur
 *   `<slug>.crayea.fr` pour poser le cookie ERP au bon scope.
 */
const TENANT_ALLOWED_AUTH_PATHS = new Set(["/auth/handoff"]);

export function AuthShellLayout() {
  // Si on est sur un sous-domaine tenant ET que la page n'est pas une
  // exception légitime, on bascule vers le hub avant tout rendu. Calculé en
  // synchrone à chaque render pour ne pas afficher le squelette d'auth
  // (logo, bulles…) avant la redirection.
  const hubBaseUrl = detectHubBaseUrl();
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const shouldRedirectToHub = hubBaseUrl !== null && !TENANT_ALLOWED_AUTH_PATHS.has(currentPath);

  useEffect(() => {
    if (!shouldRedirectToHub || !hubBaseUrl) return;
    // `replace` (vs assign) pour ne pas polluer l'historique avec une URL
    // tenant qui n'aurait pas dû être visitée.
    window.location.replace(`${hubBaseUrl}${window.location.pathname}${window.location.search}`);
  }, [shouldRedirectToHub, hubBaseUrl]);

  // Pendant la redirection (typiquement <100 ms), évite un flash de la card
  // d'auth servie depuis le mauvais host.
  if (shouldRedirectToHub) return null;

  return (
    <div className="relative min-h-screen overflow-hidden bg-champagne-50 text-champagne-900">
      {/* Bulles : densité moyenne (90), placées en arrière-plan absolu.
          Tone "light" = bulles dorées translucides adaptées au fond crème. */}
      <BubblesCanvas count={90} tone="light" />

      {/* Contenu — z-10 pour passer au-dessus des bulles */}
      <div className="relative z-10 flex min-h-screen flex-col">
        {/* Header sobre : juste le wordmark, pas de nav (utilisateur non authed) */}
        <header className="px-6 pt-8 lg:px-10 lg:pt-10">
          <a
            href="/"
            className="inline-flex items-baseline gap-2 text-champagne-900 transition hover:text-champagne-700"
          >
            <span className="text-xl font-semibold tracking-tight">Crayéa</span>
            <span className="hidden text-xs uppercase tracking-[0.18em] text-champagne-700 sm:inline">
              ERP de la Champagne
            </span>
          </a>
        </header>

        {/* Zone centrale : centrée verticalement sur desktop, top mobile.
            La page enfant rend ici sa propre AuthLayout (card titrée). */}
        <main className="flex flex-1 items-center justify-center px-6 py-10 lg:px-10">
          {/* Suspense local : le canvas de bulles + le header/footer restent
              montés pendant le chargement d'une page auth lazy (signup, reset…),
              seule la carte centrale affiche un placeholder. */}
          <Suspense
            fallback={
              <Skeleton
                variant="rect"
                width={400}
                height={340}
                className="max-w-full rounded-2xl"
              />
            }
          >
            <Outlet />
          </Suspense>
        </main>

        {/* Footer minimal — réassurance conformité + retour vitrine */}
        <footer className="px-6 pb-6 text-center text-xs text-champagne-800/60 lg:px-10">
          <p>
            Conçu pour la filière Champagne ·{" "}
            <a
              href="/"
              className="text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
            >
              Retour au site
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
